import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Tag,
  Space,
  Alert,
  Divider,
  message,
  Tooltip,
  Row,
  Col,
} from 'antd';
import {
  SaveOutlined,
  ExperimentOutlined,
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { billingConfigService } from '../services/billingConfigService';

const { Title, Text, Paragraph } = Typography;

const BillingConfig = () => {
  const [form] = Form.useForm();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await billingConfigService.get();
      setConfig(res?.data || null);
      form.setFieldsValue({
        razorpayKeyId: res?.data?.razorpayKeyId || '',
        publicBaseUrl: res?.data?.publicBaseUrl || '',
        razorpayKeySecret: '', // never echoed; user re-enters to change
        razorpayWebhookSecret: '',
      });
    } catch (err) {
      message.error(err.message || 'Failed to load billing config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (values) => {
    const payload = {
      razorpayKeyId: values.razorpayKeyId || '',
      publicBaseUrl: values.publicBaseUrl || '',
    };
    if (values.razorpayKeySecret && values.razorpayKeySecret.trim()) {
      payload.razorpayKeySecret = values.razorpayKeySecret.trim();
    }
    if (values.razorpayWebhookSecret && values.razorpayWebhookSecret.trim()) {
      payload.razorpayWebhookSecret = values.razorpayWebhookSecret.trim();
    }
    setSaving(true);
    try {
      const res = await billingConfigService.save(payload);
      setConfig(res?.data || null);
      form.setFieldsValue({ razorpayKeySecret: '', razorpayWebhookSecret: '' });
      message.success('Razorpay config saved.');
    } catch (err) {
      message.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await billingConfigService.test();
      if (res.success) {
        message.success(res.message || 'Razorpay credentials validated.');
      } else {
        message.error(res.message || 'Razorpay test failed');
      }
    } catch (err) {
      message.error(err.message || 'Razorpay test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleCopyWebhook = async () => {
    if (!config?.webhookUrl) return;
    try {
      await navigator.clipboard.writeText(config.webhookUrl);
      message.success('Webhook URL copied');
    } catch {
      message.error('Could not copy — copy manually');
    }
  };

  const activity = config?.webhookActivity;
  const lastInbound = activity?.lastInboundAt ? new Date(activity.lastInboundAt) : null;
  const lastError = activity?.lastErrorAt ? new Date(activity.lastErrorAt) : null;
  const status =
    lastError && (!lastInbound || lastError > lastInbound)
      ? 'blocked'
      : lastInbound
      ? 'ok'
      : 'idle';

  const fmt = (d) =>
    d ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <div className="space-y-6" style={{ padding: '0 4px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          <DollarOutlined style={{ marginRight: 8, color: '#3b82f6' }} />
          Razorpay Billing Configuration
        </Title>
        <Text type="secondary">
          One Razorpay account powers payments across every tenant. Save your live or test
          credentials here, then paste the generated webhook URL into the Razorpay dashboard.
        </Text>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={14}>
          <Card title="Razorpay credentials" loading={loading}>
            <Form form={form} layout="vertical" onFinish={handleSave} autoComplete="off">
              <div style={{ marginBottom: 16 }}>
                <Tag color={config?.razorpayKeyId ? 'success' : 'default'}>
                  {config?.razorpayKeyId ? 'Key ID configured' : 'Not configured'}
                </Tag>
                <Tag color={config?.hasKeySecret ? 'success' : 'default'}>
                  {config?.hasKeySecret ? 'Secret on file' : 'Secret missing'}
                </Tag>
                <Tag color={config?.hasWebhookSecret ? 'success' : 'warning'}>
                  {config?.hasWebhookSecret
                    ? 'Webhook signing on'
                    : 'Webhook signing not set'}
                </Tag>
              </div>

              <Form.Item
                name="razorpayKeyId"
                label="Razorpay Key ID"
                rules={[{ required: true, message: 'Enter your Razorpay Key ID' }]}
                extra="Public part of the key pair. Looks like rzp_test_… or rzp_live_…"
              >
                <Input placeholder="rzp_test_xxxxxxxxxxxxxxxx" autoComplete="off" />
              </Form.Item>

              <Form.Item
                name="razorpayKeySecret"
                label="Razorpay Key Secret"
                tooltip={
                  config?.hasKeySecret
                    ? 'Leave blank to keep the existing secret. Type a new value to replace it.'
                    : 'Private secret from your Razorpay dashboard.'
                }
                rules={
                  config?.hasKeySecret
                    ? []
                    : [{ required: true, message: 'Enter the Razorpay Key Secret' }]
                }
              >
                <Input.Password
                  placeholder={
                    config?.hasKeySecret
                      ? '•••• secret on file — leave blank to keep'
                      : 'Your private secret'
                  }
                  autoComplete="new-password"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>

              <Divider />

              <Form.Item
                name="publicBaseUrl"
                label="Public base URL of the tenant API"
                tooltip="e.g. https://crmpro.qodequay.com — the host Razorpay will POST webhooks to."
                rules={[
                  {
                    pattern: /^https?:\/\/.+/,
                    message: 'Must start with http:// or https://',
                  },
                ]}
                extra="Used to compose the webhook URL shown on the right. Falls back to APP_PUBLIC_BASE_URL env var when blank."
              >
                <Input placeholder="https://crmpro.qodequay.com" autoComplete="off" />
              </Form.Item>

              <Form.Item
                name="razorpayWebhookSecret"
                label="Razorpay Webhook Secret"
                tooltip={
                  config?.hasWebhookSecret
                    ? 'Leave blank to keep. The same string you set on the Razorpay dashboard webhook page.'
                    : 'Set the same string here AND on the Razorpay webhook page so we can verify signatures.'
                }
              >
                <Input.Password
                  placeholder={
                    config?.hasWebhookSecret
                      ? '•••• webhook secret on file — leave blank to keep'
                      : 'Choose any strong string and paste it on the Razorpay webhook page'
                  }
                  autoComplete="new-password"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    icon={<SaveOutlined />}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={handleTest}
                    loading={testing}
                    icon={<ExperimentOutlined />}
                    disabled={!config?.hasKeySecret}
                  >
                    Test Connection
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Webhook URL for Razorpay" loading={loading}>
            <Paragraph type="secondary">
              In your Razorpay dashboard, go to <strong>Settings → Webhooks → Add new webhook</strong>,
              paste the URL below, set the same <strong>Webhook Secret</strong> you saved on the
              left, and select the events <code>payment.captured</code> and{' '}
              <code>payment.failed</code> (at minimum).
            </Paragraph>
            <Space.Compact style={{ width: '100%' }}>
              <Input value={config?.webhookUrl || ''} readOnly />
              <Tooltip title="Copy">
                <Button icon={<CopyOutlined />} onClick={handleCopyWebhook}>
                  Copy
                </Button>
              </Tooltip>
            </Space.Compact>
            {config?.webhookUrl?.includes('localhost') && (
              <Alert
                style={{ marginTop: 12 }}
                type="warning"
                showIcon
                message="This URL points at localhost"
                description="Razorpay can't reach localhost. Set the Public base URL above (or APP_PUBLIC_BASE_URL on the tenant server) before pasting into Razorpay."
              />
            )}

            <Divider>Webhook activity</Divider>
            {status === 'idle' && (
              <Alert
                type="warning"
                showIcon
                icon={<ClockCircleOutlined />}
                message="No inbound Razorpay webhooks yet"
                description="Once Razorpay starts hitting the URL, you'll see live counts and timestamps here."
              />
            )}
            {status !== 'idle' && (
              <div>
                <Space size={8} wrap style={{ marginBottom: 12 }}>
                  {status === 'ok' && (
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                      Receiving webhooks
                    </Tag>
                  )}
                  {status === 'blocked' && (
                    <Tag color="error" icon={<CloseCircleOutlined />}>
                      Webhooks blocked
                    </Tag>
                  )}
                  <Tag>
                    {(activity?.inboundCount || 0).toLocaleString()} total
                  </Tag>
                </Space>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Last inbound</Text>
                    <div><Text strong>{fmt(lastInbound)}</Text></div>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Last rejection</Text>
                    <div><Text strong>{fmt(lastError)}</Text></div>
                  </div>
                </div>
                {status === 'blocked' && activity?.lastErrorReason && (
                  <Alert
                    style={{ marginTop: 12 }}
                    type="error"
                    showIcon
                    message="Razorpay is reaching us but the request is rejected"
                    description={activity.lastErrorReason}
                  />
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default BillingConfig;
